<template>
  <div class="api-search-form">
    <div class="search-panel">

      <!-- Erreur -->
      <div v-if="error" class="error-container">
        <el-alert :title="error" type="error" :closable="false" />
      </div>

      <!-- Formulaire de recherche -->
      <div v-else-if="formConfig" class="search-container">
        <!-- Champ de recherche et filtres actifs alignés -->
        <div class="filters-row" :class="{ 'centered': centered }">

          <!-- Champ de recherche -->
          <div v-if="!hideSearchField" class="search-field-container">
            <el-input
              v-model="searchValue"
              :placeholder="searchPlaceholder || $t('general.filters.openSearchPlaceholder')"
              size="small"
              style="width: 280px;"
              @input="handleSearchInput"
              @keydown.enter.native="handleSearchEnter"
            >
              <i slot="append" class="el-icon-search" @click="handleSearch" style="cursor: pointer;" />
            </el-input>
          </div>

          <!-- Boutons de filtres -->
          <div v-for="button in visibleFilterButtons" :key="button.key" class="filter-button-container">
            <el-button
              size="small"
              :class="['filter-btn', { 'has-value': hasValue(button.key) }]"
              @click="toggleFilter(button, $event)"
            >
              {{ getButtonDisplayText(button) }}
              <i
                v-if="hasValue(button.key)"
                class="el-icon-close clear-btn"
                @click.stop="clearFilter(button.key)"
              />
            </el-button>

            <!-- Popup du filtre -->
            <div v-if="activePopup === button.key" class="filter-popup-overlay" @click="handleOverlayClick">
              <div
                class="filter-popup"
                :class="{ 'cascader-popup': isCascaderPopup(button) }"
                @click.stop
                :style="popupStyle"
              >
                <component
                  :is="getComponentForField(button.field)"
                  :key="`${button.key}-${resetCounter}`"
                  :field="button.field"
                  :field-config="button.field"
                  :field-name="button.field.name"
                  :value="filterValues[button.key]"
                  :form-filters="filterValues"
                  :business-domain="businessDomain"
                  :user-context="userContext"
                  :is-popup-mode="true"
                  @input="updateFilter(button.key, $event)"
                  @change="handleFieldChange"
                  @range-update="handleRangeUpdate"
                  @month-change="handleMonthChange"
                  @popup-closed="handlePopupClosed"
                  @close="closePopup"
                  @close-and-search="handleCloseAndSearch"
                />
              </div>
            </div>
          </div>

          <!-- Lien "Plus..." pour gérer les filtres épinglés -->
          <el-link
            v-if="!hideMoreButton && hasFiltersForPinning"
            type="primary"
            @click="showPinningModal = true"
            style="font-size: 14px; margin-left: 8px; color: #67c23a;"
          >
            Plus...
          </el-link>

          <!-- Lien "Réinitialiser les filtres" -->
          <el-link
            type="primary"
            @click="resetFilters"
            style="font-size: 14px; margin-left: 12px; color: #f56c6c;"
          >
            {{ $t('general.filters.resetFilters') }}
          </el-link>
        </div>
      </div>

      <!-- Modal d'épinglage des filtres -->
      <el-dialog
        :title="$t('general.filters.managePinnedFilters')"
        :visible.sync="showPinningModal"
        width="500px"
        :close-on-click-modal="false"
      >
        <div style="margin-bottom: 16px;">
          <p style="color: #606266; font-size: 14px; margin-bottom: 16px;">
            {{ $t('general.filters.pinnedFiltersExplanation') }}
          </p>
          <div v-for="filter in availableFiltersForPinning" :key="filter.key" style="margin-bottom: 8px;">
            <el-checkbox
              :value="tempPinnedFilters.includes(filter.key)"
              @input="checked => toggleTempPinnedFilter(filter.key, checked)"
            >
              <i class="el-icon-thumb-tack" v-if="tempPinnedFilters.includes(filter.key)"></i>
              <span>{{ filter.label }}</span>
            </el-checkbox>
          </div>
        </div>
        <div slot="footer" class="dialog-footer">
          <el-button @click="cancelPinnedFilters()">{{ $t('users.cancel') }}</el-button>
          <el-button type="primary" @click="confirmPinnedFilters()">
            {{ $t('users.confirm') }}
          </el-button>
        </div>
      </el-dialog>

    </div>
  </div>
</template>

<script>
import { searchFormApi } from '@/services/searchForm/api'
import { convertApiFiltersToStore } from '@/utils/filterConverter'
import { buildCompleteSearchFormContext } from '@/services/searchForm/contextBuilder'
import { getContactManagerResetValues } from '@/services/contactManager/resetFilters'
import { capitalizeFirstLetter } from '@/helpers/capitalized'

// Composants de champs
import GenericFieldText from './fields/GenericFieldText.vue'
import GenericFieldRadio from './fields/GenericFieldRadio.vue'
import GenericFieldSelect from './fields/GenericFieldSelect.vue'
import GenericFieldDateRange from './fields/GenericFieldDateRange.vue'
import GenericFieldMonthPicker from './fields/GenericFieldMonthPicker.vue'
import GenericFieldRange from './fields/GenericFieldRange.vue'
import GenericFieldPopup from './GenericFieldPopup.vue'
import GenericFieldObjectTypes from './fields/GenericFieldObjectTypes.vue'
import GenericFieldObjectTypesCheckbox from './fields/GenericFieldObjectTypesCheckbox.vue'
import GenericFieldOfferTypes from './fields/GenericFieldOfferTypes.vue'

export default {
  name: 'ApiSearchForm',
  components: {
    GenericFieldText,
    GenericFieldRadio,
    GenericFieldSelect,
    GenericFieldDateRange,
    GenericFieldMonthPicker,
    GenericFieldRange,
    GenericFieldPopup,
    GenericFieldObjectTypes,
    GenericFieldObjectTypesCheckbox,
    GenericFieldOfferTypes
  },
  props: {
    businessDomain: {
      type: String,
      default: 'immo'
    },
    route: {
      type: String,
      required: true
    },
    userContext: {
      type: Object,
      default: () => ({})
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    realtime: {
      type: Boolean,
      default: false
    },
    //  Props pour gérer le loader global
    globalLoading: {
      type: Boolean,
      default: false
    },
    //  Données pré-chargées par le composant parent (users, contacts, etc.)
    cachedData: {
      type: Object,
      default: () => ({})
    },
    // Masquer le champ de recherche texte libre
    hideSearchField: {
      type: Boolean,
      default: false
    },
    // Placeholder personnalisé du champ de recherche
    searchPlaceholder: {
      type: String,
      default: null
    },
    // Masquer le lien "Plus..." pour épingler les filtres
    hideMoreButton: {
      type: Boolean,
      default: false
    },
    // Centrer le formulaire (utile quand peu de champs)
    centered: {
      type: Boolean,
      default: false
    },
    //  Nom du store Vuex à utiliser pour la sauvegarde/chargement du tri
    storeName: {
      type: String,
      default: null  // Si null, on détectera automatiquement avec businessDomain (rétro-compatibilité)
    }
  },
  data() {
    return {
      error: null,
      activePopup: null,
      popupStyle: {},

      formConfig: null,
      filterValues: {},
      searchValue: '',

      // Compteur pour forcer le re-render des composants lors du reset
      resetCounter: 0,

      // Gestion des filtres épinglés
      showPinningModal: false,
      pinnedFilters: [],
      tempPinnedFilters: [],

      // Observer pour les dropdowns Element UI
      dropdownObserver: null,

      // Sauvegarde des valeurs avant ouverture de popup pour détecter les changements
      popupInitialValues: {},

      // Gestion du blocage du scroll
      isMainScrollBlocked: false,
      scrollContainer: null,
      savedScrollTop: 0,
      savedOverflow: '',
      savedBodyOverflow: '',
      savedHtmlOverflow: '',
      preventScrollHandler: null,

      // Fonction pour désactiver le watcher du tri
      sortWatcherUnwatch: null
    }
  },
  computed: {
    //  Nom du store à utiliser (générique)
    activeStoreName() {
      // Si une prop storeName est fournie, l'utiliser
      if (this.storeName) {
        return this.storeName
      }

      // Sinon, fallback sur businessDomain pour rétro-compatibilité
      return this.businessDomain === 'vo' ? 'postsVo' : 'postsListImmo'
    },

    // Tous les boutons de filtres disponibles
    allFilterButtons() {
      if (!this.formConfig || !this.formConfig.sections) return []

      const filtersSection = this.formConfig.sections.find(s => s.code === 'filters')
      if (!filtersSection) return []

      return filtersSection.fields
        .filter(field => field.name !== 'search' && this.isVisible(field))
        .map(field => ({
          key: field.name,
          label: this.translate(field.dynamicLabel || field.label || field.name),
          field: field
        }))
    },

    // Boutons de filtres visibles (épinglés)
    visibleFilterButtons() {
      return this.allFilterButtons.filter(button =>
        this.pinnedFilters.includes(button.key)
      )
    },

    // Filtres disponibles pour l'épinglage
    availableFiltersForPinning() {
      return this.allFilterButtons
    },

    // Vérifier s'il y a des filtres disponibles pour l'épinglage
    hasFiltersForPinning() {
      return this.availableFiltersForPinning.length > 0
    },

    /**
     * Contexte utilisé par visibility_rules (users_count, contacts_count,
     * clés plates comme "user.roles", etc.) — doit rester aligné avec buildContext() /config.
     */
    visibilityRulesContext() {
      try {
        return buildCompleteSearchFormContext(this.$route, this.$store, this.cachedData || {})
      } catch (e) {
        console.warn('[ApiSearchForm] visibilityRulesContext:', e)
        return {}
      }
    }
  },
  watch: {
    //  Synchroniser filterValues avec les filtres du store
    initialFilters: {
      handler(newFilters) {
        if (newFilters && Object.keys(newFilters).length > 0) {
          // Fusionner les nouveaux filtres avec les filtres locaux
          this.filterValues = { ...this.filterValues, ...newFilters }
          // withoutPictures est dérivé du preset, pas un champ du formulaire
          if (this.filterValues.preset !== 'withoutPictures') {
            this.$delete(this.filterValues, 'withoutPictures')
          }
        }
      },
      deep: true,
      immediate: false
    },
    // Watcher sur userContext pour réévaluer la visibilité des champs
    userContext: {
      handler(newContext, oldContext) {
        // Vérifier si des propriétés importantes ont changé (comme activeName)
        if (JSON.stringify(newContext) !== JSON.stringify(oldContext)) {
          console.log('🔄 [ApiSearchForm] userContext changé, réévaluation de la visibilité des champs')
          // Forcer le re-rendu pour réévaluer isVisible() sur tous les champs
          this.$forceUpdate()
        }
      },
      deep: true,
      immediate: false
    },
    cachedData: {
      handler() {
        // users / contacts arrivent souvent après le premier render : réévaluer les visibility_rules
        this.$nextTick(() => this.$forceUpdate())
      },
      deep: true
    },
    showPinningModal(newVal) {
      if (newVal) {
        this.tempPinnedFilters = [...this.pinnedFilters]
      }
    }
  },
  async mounted() {
    await this.loadConfig()
    this.initValues()
    await this.loadSavedFilters()  // CORRECTION : Attendre le chargement des filtres sauvegardés
    this.loadPinnedFilters()

    // ✅ Watcher dynamique pour le tri du store actif
    this.setupSortWatcher()

    // Observer les dropdowns Element UI créés dynamiquement
    this.setupDropdownObserver()
    this.setupElementUIEventListeners()

    // Écouter la touche Escape pour fermer les popups avec recherche
    this.setupKeyboardListeners()

    // Identifier le conteneur de scroll principal après que tout soit monté
    this.$nextTick(() => {
      setTimeout(() => {
        this.findScrollContainer()
      }, 500)
    })

    // Émettre ready quand tout est prêt
    this.$emit('ready')

    // Forcer le re-rendu des composants pour qu'ils voient les nouvelles valeurs
    this.$forceUpdate()

    // Déclencher automatiquement la recherche initiale avec les valeurs chargées
    this.$nextTick(() => {
      this.emitSearch()
    })
  },
  methods: {
    // === CONFIGURATION ===
    async loadConfig() {
      try {
        // Émettre événement pour activer le loader global
        this.$emit('loading-start', 'Chargement des filtres...')
        this.error = null

        const payload = {
          businessDomain: this.businessDomain,
          route: this.route,
          userContext: this.userContext,
          additionalContext: this.buildContext(),
          currentFilters: this.filterValues
        }

        const response = await searchFormApi.getSearchFormConfig(payload)

        if (response.success) {
          this.formConfig = response.data
          this.$emit('config-loaded', this.formConfig)
        } else {
          throw new Error(response.message || 'Erreur de configuration')
        }
      } catch (error) {
        this.error = error.message
        this.$emit('config-error', error)
        //  Émettre loading-end en cas d'erreur pour débloquer le loader
        this.$emit('loading-end')
      }
    },

    //  Configurer le watcher dynamique pour le tri du store actif
    setupSortWatcher() {
      // Vérifier que le store et le getter existent avant de créer le watcher
      if (!this.$store.state[this.activeStoreName]) {
        console.warn(`[ApiSearchForm] Store "${this.activeStoreName}" non trouvé, tri non surveillé`)
        return
      }

      if (!this.$store.getters[`${this.activeStoreName}/getSort`]) {
        console.warn(`[ApiSearchForm] Getter "${this.activeStoreName}/getSort" non trouvé, tri non surveillé`)
        return
      }

      // Créer un watcher dynamique pour le tri du store actif
      this.sortWatcherUnwatch = this.$watch(
        () => {
          const storeState = this.$store.state[this.activeStoreName]
          return storeState && storeState.filter ? storeState.filter.sort : undefined
        },
        (newSort, oldSort) => {
          // Éviter les boucles infinies et sauvegarder seulement si le tri a changé
          if (newSort !== oldSort && oldSort !== undefined) {
            this.saveFilters()
          }
        }
      )
    },

    buildContext() {
      const baseContext = {
        route_name: this.$route.name,
        ...this.userContext
      }

      //  GÉNÉRIQUE : Utiliser le service externe pour construire le contexte complet
      // En passant les données pré-chargées (users, contacts) si disponibles
      const additionalContext = buildCompleteSearchFormContext(this.$route, this.$store, this.cachedData)

      const finalContext = {
        ...baseContext,
        ...additionalContext
      }

      return finalContext
    },

    // === VALEURS ===
    initValues() {

      // Utiliser EXCLUSIVEMENT les valeurs par défaut du backend
      if (this.formConfig && this.formConfig.defaultValues) {
        this.filterValues = { ...this.formConfig.defaultValues }
        this.searchValue = this.filterValues.search || ''

        // GÉNÉRIQUE : Synchroniser les champs avec eventMapping dès l'initialisation
        this.syncEventMappingFields()
      } else {
        this.filterValues = {}
        this.searchValue = ''
        console.log(' [initValues] Aucune valeur par défaut trouvée, filterValues vide')
      }
    },

    // GÉNÉRIQUE : Synchroniser les champs avec leurs eventMapping (minField/maxField)
    syncEventMappingFields() {
      if (!this.formConfig || !this.formConfig.sections) return

      this.formConfig.sections.forEach(section => {
        if (section.fields) {
          section.fields.forEach(field => {
            if (field.eventMapping) {
              const mapping = typeof field.eventMapping === 'string'
                ? JSON.parse(field.eventMapping)
                : field.eventMapping

              const minValue = this.filterValues[mapping.minField]
              const maxValue = this.filterValues[mapping.maxField]
              const hasMin = minValue !== null && minValue !== undefined && minValue !== ''
              const hasMax = maxValue !== null && maxValue !== undefined && maxValue !== ''

              if (!hasMin && !hasMax) {
                return
              }

              const fieldTypeCode = field.fieldType && field.fieldType.code
              const isDateField = fieldTypeCode === 'daterange' || fieldTypeCode === 'monthpicker'
                || ['dateCreation', 'dateRange', 'monthPicker'].includes(field.name)

              if (isDateField) {
                if (hasMin && hasMax) {
                  this.$set(this.filterValues, field.name, [minValue, maxValue])
                }
              } else {
                const rangeObject = {}
                if (hasMin) rangeObject[mapping.minField] = minValue
                if (hasMax) rangeObject[mapping.maxField] = maxValue
                this.$set(this.filterValues, field.name, rangeObject)
              }
            }
          })
        }
      })
    },

    updateFilter(fieldName, value) {
      this.$set(this.filterValues, fieldName, value)
      // Ne pas déclencher automatiquement la recherche
      // L'utilisateur doit cliquer sur "Rechercher"
    },

    //  Gérer les événements range-update pour les champs de plage (dates, prix, surface)
    handleRangeUpdate(rangeData) {
      if (!rangeData || typeof rangeData !== 'object') {
        return // Ignorer les données invalides
      }

      const { field, minField, maxField, minValue, maxValue } = rangeData

      // Pour les dates (dateCreation, dateRange, monthPicker, etc.), on stocke le format tableau [startDate, endDate]
      if (field === 'dateCreation' || field === 'dateRange' || field === 'monthPicker') {
        const dateArray = minValue && maxValue ? [minValue, maxValue] : null
        this.$set(this.filterValues, field, dateArray)

        console.log(`📅 [handleRangeUpdate] Set ${field} to:`, dateArray)

        // Pour le monthPicker, on stocke aussi startDate et endDate séparément
        if (field === 'monthPicker' && minField && maxField) {
          this.$set(this.filterValues, minField, minValue || null)
          this.$set(this.filterValues, maxField, maxValue || null)
          console.log(`🗓️ [handleRangeUpdate] Month picker - Set ${minField} = ${minValue}, ${maxField} = ${maxValue}`)
        }

        // Si eventMapping est défini, stocker aussi les valeurs mappées
        const fieldConfig = this.getFieldConfig(field)
        if (fieldConfig && fieldConfig.eventMapping) {
          const { minField: mappedMinField, maxField: mappedMaxField } = fieldConfig.eventMapping
          console.log(`📍 [handleRangeUpdate] EventMapping found:`, { mappedMinField, mappedMaxField })
          if (mappedMinField) {
            this.$set(this.filterValues, mappedMinField, minValue || null)
            console.log(`  ✅ Set ${mappedMinField} =`, minValue)
          }
          if (mappedMaxField) {
            this.$set(this.filterValues, mappedMaxField, maxValue || null)
            console.log(`  ✅ Set ${mappedMaxField} =`, maxValue)
          }
        }

        console.log('📦 [handleRangeUpdate] Final filterValues:', { ...this.filterValues })
      }
      // Pour surface and price, on stocke le format objet {fieldGt, fieldLt}
      else if (field === 'surface' || field === 'price') {
        const rangeObject = {}
        if (minValue !== null && minValue !== undefined) {
          rangeObject[minField] = minValue
        }
        if (maxValue !== null && maxValue !== undefined) {
          rangeObject[maxField] = maxValue
        }

        const finalValue = Object.keys(rangeObject).length > 0 ? rangeObject : null
        this.$set(this.filterValues, field, finalValue)
      }
    },

    // Gérer l'événement month-change spécifique au MonthPicker
    handleMonthChange(monthData) {
      console.log('🗓️ [ApiSearchForm.handleMonthChange] Received:', monthData)

      // Propager l'événement au composant parent (Calendar.vue) pour synchroniser le focus
      this.$emit('month-change', monthData)
    },

    //  Gérer les événements change des champs pour les cascaders/objectTypes/checkbox
    handleFieldChange(changeData) {
      const { field, value } = changeData

      // Trouver la configuration du champ
      const fieldConfig = this.getFieldConfig(field)
      if (!fieldConfig) return

      // Pour les cascaders/objectTypes, choix multiples et checkbox, déclencher la recherche sans fermer la popup
      const isCascader = fieldConfig.component === 'GenericFieldObjectTypes' ||
                         fieldConfig.component === 'SearchFieldObjectTypes'
      const isMultipleSelect = fieldConfig.fieldType && fieldConfig.fieldType.code === 'multiselect'
      const isCheckboxGroup = fieldConfig.selectionMode === 'multiple'
        && fieldConfig.component !== 'GenericFieldRadio'
      const isObjectTypesCheckbox = fieldConfig.component === 'GenericFieldObjectTypesCheckbox'

      if (isCascader || isMultipleSelect || isCheckboxGroup || isObjectTypesCheckbox) {
        this.saveFilters()
        this.emitSearch()
      }
    },

    clearFilter(fieldName) {
      //  Supprimer complètement le champ au lieu de le mettre à null
      this.$delete(this.filterValues, fieldName)

      //  Vider aussi les champs dépendants de ce champ
      if (this.formConfig && this.formConfig.sections) {
        this.formConfig.sections.forEach(section => {
          section.fields.forEach(field => {
            if (field.name === fieldName && field.properties && field.properties.hasDependents) {
              const dependents = field.properties.hasDependents
              dependents.forEach(dependentField => {
                this.$delete(this.filterValues, dependentField)
              })
            }
            if (field.name === fieldName && field.eventMapping) {
              const mapping = typeof field.eventMapping === 'string'
                ? JSON.parse(field.eventMapping)
                : field.eventMapping
              if (mapping.minField) this.$delete(this.filterValues, mapping.minField)
              if (mapping.maxField) this.$delete(this.filterValues, mapping.maxField)
            }
          })
        })
      }

      //  GÉNÉRIQUE : Reconvertir tous les filtres pour nettoyer les champs dérivés
      // C'est la seule façon vraiment générique de gérer tous les cas
      const convertedFilters = convertApiFiltersToStore(this.filterValues, {
        metier: this.userContext.metier || (this.$store && this.$store.getters && this.$store.getters.getSitimmoUser && this.$store.getters.getSitimmoUser.metier)
      })

      if (this.formConfig && this.formConfig.sections) {
        this.formConfig.sections.forEach(section => {
          section.fields.forEach(field => {
            if (field.name === fieldName && field.eventMapping) {
              const mapping = typeof field.eventMapping === 'string'
                ? JSON.parse(field.eventMapping)
                : field.eventMapping
              if (mapping.minField) convertedFilters[mapping.minField] = undefined
              if (mapping.maxField) convertedFilters[mapping.maxField] = undefined
            }
          })
        })
      }

      // Utiliser le store actif
      this.$store.commit(`${this.activeStoreName}/addFilter`, convertedFilters)

      //  Déclencher la recherche après suppression du filtre
      this.saveFilters()
      this.emitSearch()
    },

    resetFilters() {
      // Réinitialiser TOUS les champs, pas seulement ceux dans defaultValues
      if (this.formConfig) {
        // 1. Vider complètement filterValues ET nettoyer les champs problématiques
        this.filterValues = {}

        // 2. Nettoyer explicitement les champs qui peuvent poser problème
        const problematicFields = ['annoncesModeles', 'annoncesActivees', 'annoncesDesactivees', 'annoncesVendues', 'annoncesSupprimees', 'negociateur', 'negociateur_code']
        problematicFields.forEach(field => {
          this.filterValues[field] = null
        })

        // 3. Nettoyer les champs mappés via eventMapping (beforeContacts, afterContacts, etc.)
        if (this.formConfig.sections) {
          this.formConfig.sections.forEach(section => {
            if (section.fields) {
              section.fields.forEach(field => {
                // Si le champ a un eventMapping, vider aussi les champs mappés
                if (field.eventMapping) {
                  const mapping = typeof field.eventMapping === 'string'
                    ? JSON.parse(field.eventMapping)
                    : field.eventMapping
                  if (mapping.minField) {
                    this.filterValues[mapping.minField] = null
                  }
                  if (mapping.maxField) {
                    this.filterValues[mapping.maxField] = null
                  }
                }
              })
            }
          })
        }

        // 4. Appliquer les defaultValues du backend s'ils existent
        if (this.formConfig.defaultValues) {
          this.filterValues = { ...this.filterValues, ...this.formConfig.defaultValues }
        }

        // 5 Appliquer les valeurs de réinitialisation spécifiques (ex: Contact Manager)
        const resetValues = getContactManagerResetValues(this.formConfig.templateCode)
        if (resetValues) {
          this.filterValues = { ...this.filterValues, ...resetValues }
        }

        // 6 GÉNÉRIQUE : Synchroniser les champs avec eventMapping
        this.syncEventMappingFields()

        // 7. Pour tous les champs de la config, s'assurer qu'ils ont une valeur par défaut
        if (this.formConfig.sections) {
          this.formConfig.sections.forEach(section => {
            if (section.fields) {
              section.fields.forEach(field => {
                if (this.filterValues[field.name] === undefined) {
                  // Définir la valeur par défaut selon le type de champ
                  const fieldTypeCode = field.fieldType && field.fieldType.code ? field.fieldType.code : field.fieldType

                  if (fieldTypeCode === 'text' || fieldTypeCode === 'select') {
                    this.filterValues[field.name] = ''
                  } else if (fieldTypeCode === 'select') {
                    // Les select doivent être null pour ne pas être envoyés dans l'API si non sélectionnés
                    this.filterValues[field.name] = null
                  } else if (fieldTypeCode === 'daterange' || fieldTypeCode === 'range' || fieldTypeCode === 'monthpicker') {
                    this.filterValues[field.name] = null
                  } else if (fieldTypeCode === 'checkbox_group' || fieldTypeCode === 'cascader') {
                    this.filterValues[field.name] = []
                  } else if (fieldTypeCode === 'radio_group') {
                    this.filterValues[field.name] = null
                  } else {
                    this.filterValues[field.name] = null
                  }
                }
              })
            }
          })
        }

        this.searchValue = this.filterValues.search || ''

        //  Incrémenter le compteur de reset pour forcer le re-render des composants
        this.resetCounter++

        //  Forcer la réactivité Vue pour mettre à jour les boutons
        this.$nextTick(() => {
          this.$forceUpdate()
        })

        this.saveFilters()

        const cleanedFilters = convertApiFiltersToStore(this.filterValues, {
          metier: this.userContext.metier || (this.$store && this.$store.getters && this.$store.getters.getSitimmoUser && this.$store.getters.getSitimmoUser.metier)
        })
        this.$emit('reset', cleanedFilters)
        this.emitSearch()
      }
    },

    hasValue(fieldName) {
      const value = this.filterValues[fieldName]


      // Cas null, undefined, string vide
      if (value === null || value === undefined || value === '') {
        return false
      }

      // Cas array vide
      if (Array.isArray(value)) {
        const hasVal = value.length > 0
        return hasVal
      }

      // Cas objet range - vérifier s'il contient des valeurs
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Trouver le type de champ dans la configuration
        const fieldConfig = this.getFieldConfig(fieldName)
        if (fieldConfig && fieldConfig.fieldType === 'range') {
          const keys = Object.keys(value)
          if (keys.length === 0) return false

          // Vérifier si au moins une valeur n'est pas null/undefined
          return keys.some(key => value[key] !== null && value[key] !== undefined)
        }

        // Pour les autres objets, vérifier s'ils ont des propriétés non vides
        const keys = Object.keys(value)
        if (keys.length === 0) return false
        return keys.some(key => value[key] !== null && value[key] !== undefined && value[key] !== '')
      }

      // Cas général : objet ou valeur primitive non vide
      return true
    },

    /**
     * Récupère la configuration d'un champ spécifique
     * @param {string} fieldName - Nom du champ
     * @returns {Object|null} Configuration du champ ou null si non trouvé
     */
    getFieldConfig(fieldName) {
      if (!this.formConfig || !this.formConfig.sections) {
        return null
      }

      for (const section of this.formConfig.sections) {
        if (section.fields) {
          const field = section.fields.find(f => f.name === fieldName)
          if (field) {
            return field
          }
        }
      }

      return null
    },

    /**
     * Réinitialise la valeur d'un filtre à sa valeur par défaut
     * @param {string} filterKey - Nom du filtre à réinitialiser
     */
    resetFilterValue(filterKey) {
      const fieldConfig = this.getFieldConfig(filterKey)
      if (!fieldConfig) return

      // Récupérer la valeur par défaut du champ
      const defaultValue = (this.formConfig && this.formConfig.defaultValues)
        ? this.formConfig.defaultValues[filterKey]
        : undefined

      // Si une valeur par défaut existe, l'utiliser
      if (defaultValue !== undefined && defaultValue !== null) {
        this.$set(this.filterValues, filterKey, defaultValue)
        return
      }

      // Sinon, supprimer complètement la clé pour éviter d'envoyer des valeurs vides
      this.$delete(this.filterValues, filterKey)
    },

    // === SAUVEGARDE/CHARGEMENT ===
    async saveFilters() {
      try {
        // Récupérer le tri actuel depuis le store (si disponible)
        let currentSort = null
        if (this.$store.getters[`${this.activeStoreName}/getSort`]) {
          currentSort = this.$store.getters[`${this.activeStoreName}/getSort`]
        }

        const payload = {
          page: this.route,
          filters: this.filterValues,
          pinnedFilters: this.pinnedFilters,
          sort: currentSort  // ✅ Inclure le tri
        }

        // Pour les comptes NON multi-utilisateurs : sauvegarder dans localStorage
        if (!this.$store.getters.hasMultiAccounts) {
          const localKey = `saved_filters_${this.route}`
          const dataToSave = {
            filters: this.filterValues,
            pinnedFilters: this.pinnedFilters,
            sort: currentSort  // ✅ Inclure le tri
          }
          localStorage.setItem(localKey, JSON.stringify(dataToSave))
        } else {
          // Pour les comptes multi-utilisateurs : sauvegarder via l'API backend
          await searchFormApi.saveFilters(payload)
          console.log(' Filtres sauvegardés sur le backend')
        }
      } catch (error) {
        console.error('Erreur sauvegarde filtres:', error)
      }
    },

    async loadSavedFilters() {
      // Vérifier que formConfig existe avant de continuer
      if (!this.formConfig) {
        console.warn('[loadSavedFilters] formConfig non chargé, impossible de charger les filtres')
        return
      }

      const defaultValues = this.formConfig.defaultValues || {}

      // Extraire les dates du store global pour Contact Manager (priorité absolue)
      const contextDates = {}
      if (defaultValues.beforeContacts) {
        contextDates.beforeContacts = defaultValues.beforeContacts
      }
      if (defaultValues.afterContacts) {
        contextDates.afterContacts = defaultValues.afterContacts
      }

      // Pour les comptes NON multi-utilisateurs : charger depuis localStorage
      if (!this.$store.getters.hasMultiAccounts) {
        try {
          const localKey = `saved_filters_${this.route}`
          const savedDataStr = localStorage.getItem(localKey)

          if (savedDataStr) {
            const savedData = JSON.parse(savedDataStr)

            //  CAS 1 : Filtres sauvegardés trouvés
            const savedFilters = savedData.filters || {}

            // Fusionner : valeurs par défaut + filtres sauvegardés (les sauvegardés ont priorité) + dates contexte (priorité absolue)
            this.filterValues = { ...defaultValues, ...savedFilters, ...contextDates }
            this.searchValue = this.filterValues.search || ''

            // GÉNÉRIQUE : Synchroniser les champs avec eventMapping
            this.syncEventMappingFields()

            if (savedData.pinnedFilters) {
              this.pinnedFilters = savedData.pinnedFilters
            }
            this.ensureConditionalFiltersPinned()

            // ✅ Restaurer le tri dans le store (si le getter existe)
            if (savedData.sort && this.$store.getters[`${this.activeStoreName}/getSort`] !== undefined) {
              this.$store.commit(`${this.activeStoreName}/setSort`, savedData.sort)
            }
          } else {
            //  CAS 2 : Aucun filtre sauvegardé - utiliser les valeurs par défaut
            this.filterValues = { ...defaultValues, ...contextDates }
            this.searchValue = this.filterValues.search || ''

            // GÉNÉRIQUE : Synchroniser les champs avec eventMapping
            this.syncEventMappingFields()

            this.loadDefaultPinnedFilters()
          }
        } catch (error) {
          console.warn('Erreur chargement filtres localStorage:', error.message)
          // Fallback vers les filtres par défaut
          this.filterValues = { ...defaultValues, ...contextDates }

          // GÉNÉRIQUE : Synchroniser les champs avec eventMapping
          this.syncEventMappingFields()

          this.loadDefaultPinnedFilters()
        }
        return
      }

      // Pour les comptes multi-utilisateurs : charger depuis l'API backend
      try {
        const response = await searchFormApi.loadFilters({ page: this.route })

        if (response.success) {
          if (response.data) {
            //  CAS 1 : Filtres sauvegardés trouvés
            const savedFilters = response.data.filters || {}

            // Fusionner : valeurs par défaut + filtres sauvegardés (les sauvegardés ont priorité) + dates contexte (priorité absolue)
            this.filterValues = { ...defaultValues, ...savedFilters, ...contextDates }
            this.searchValue = this.filterValues.search || ''

            // GÉNÉRIQUE : Synchroniser les champs avec eventMapping
            this.syncEventMappingFields()

            if (response.data.pinnedFilters) {
              this.pinnedFilters = response.data.pinnedFilters
            }
            this.ensureConditionalFiltersPinned()

            // ✅ Restaurer le tri dans le store (si le getter existe)
            if (response.data.sort && this.$store.getters[`${this.activeStoreName}/getSort`] !== undefined) {
              this.$store.commit(`${this.activeStoreName}/setSort`, response.data.sort)
            }

            console.log(' Filtres chargés depuis le backend')
          } else {
            //  CAS 2 : Aucun filtre sauvegardé (data: null) - utiliser les valeurs par défaut
            this.filterValues = { ...defaultValues, ...contextDates }
            this.searchValue = this.filterValues.search || ''

            // GÉNÉRIQUE : Synchroniser les champs avec eventMapping
            this.syncEventMappingFields()

            this.loadDefaultPinnedFilters()
          }
        }
      } catch (error) {
        // Fallback vers les filtres par défaut
        this.filterValues = { ...defaultValues, ...contextDates }

        // GÉNÉRIQUE : Synchroniser les champs avec eventMapping
        this.syncEventMappingFields()

        this.loadDefaultPinnedFilters()
      }
    },
    // === FILTRES ÉPINGLÉS ===
    loadPinnedFilters() {
      // Les filtres épinglés sont maintenant chargés via l'API backend
      // Si pas encore chargés, utiliser les défauts
      if (!this.pinnedFilters || this.pinnedFilters.length === 0) {
        this.loadDefaultPinnedFilters()
      }
    },

    /**
     * Ajoute aux filtres épinglés tout champ de la section "filters" qui porte des
     * visibility_rules (affichage conditionnel côté front). Souvent absents des pinnedFilters API.
     */
    ensureConditionalFiltersPinned() {
      if (!this.formConfig || !this.formConfig.sections) {
        return
      }
      const filtersSection = this.formConfig.sections.find(section => section.code === 'filters')
      if (!filtersSection || !Array.isArray(filtersSection.fields)) {
        return
      }

      filtersSection.fields.forEach(field => {
        if (!field || !field.name || field.name === 'search') {
          return
        }
        const hasVisibilityRules = Array.isArray(field.visibility_rules) && field.visibility_rules.length > 0
        if (!hasVisibilityRules) {
          return
        }
        if (!this.pinnedFilters.includes(field.name)) {
          this.pinnedFilters.push(field.name)
        }
      })
    },

    loadDefaultPinnedFilters() {
      // Utiliser les filtres épinglés du backend
      if (this.formConfig && this.formConfig.pinnedFilters) {
        this.pinnedFilters = [...this.formConfig.pinnedFilters]
      } else {
        this.pinnedFilters = []
      }
      this.ensureConditionalFiltersPinned()
    },

    async savePinnedFilters() {
      // Sauvegarder via l'API backend avec les filtres
      await this.saveFilters()
    },

    toggleTempPinnedFilter(filterKey, checked) {
      if (checked) {
        if (!this.tempPinnedFilters.includes(filterKey)) {
          this.tempPinnedFilters.push(filterKey)
        }
      } else {
        const index = this.tempPinnedFilters.indexOf(filterKey)
        if (index > -1) {
          this.tempPinnedFilters.splice(index, 1)
        }
      }
    },

    confirmPinnedFilters() {
      // Détecter les filtres qui ont été désépinglés
      const unpinnedFilters = this.pinnedFilters.filter(key => !this.tempPinnedFilters.includes(key))

      // Réinitialiser les valeurs des filtres désépinglés
      unpinnedFilters.forEach(filterKey => {
        this.resetFilterValue(filterKey)
      })

      this.pinnedFilters = [...this.tempPinnedFilters]
      this.savePinnedFilters()
      this.showPinningModal = false

      // Émettre la recherche si des filtres ont été désépinglés
      if (unpinnedFilters.length > 0) {
        this.emitSearch()
      }
    },

    cancelPinnedFilters() {
      this.tempPinnedFilters = [...this.pinnedFilters]
      this.showPinningModal = false
    },

    // === POPUPS ===
    toggleFilter(button, event) {
      if (this.activePopup === button.key) {
        this.closePopupWithSearch()
      } else {
        // Sauvegarder les valeurs actuelles avant d'ouvrir la popup
        this.savePopupInitialValues(button.key)

        this.activePopup = button.key
        this.calculatePopupPosition(event)

        // Bloquer le scroll dès qu'une popup s'ouvre
        this.$nextTick(() => {
          // Re-détecter le conteneur au cas où
          this.findScrollContainer()
          // Bloquer le scroll principal
          this.blockMainScroll()
        })

        // Forcer le z-index des dropdowns après ouverture
        setTimeout(() => this.forceDropdownZIndex(), 100)
      }
    },

    closePopup() {
      // Fermeture normale sans recherche automatique
      this.activePopup = null
      this.popupStyle = {}
      // Nettoyer les valeurs initiales sauvegardées
      this.popupInitialValues = {}

      // Débloquer le scroll quand la popup se ferme
      this.unblockMainScroll()
    },

    // Gérer le clic sur l'overlay (fond transparent)
    handleOverlayClick() {
      this.closePopupWithSearch()
    },

    // Trouver le conteneur de scroll principal
    findScrollContainer() {
      // Chercher le conteneur avec la classe 'content' ou similaire
      // Fallback sur le body si non trouvé
      const possibleContainers = [
        document.querySelector('.app-main'),
        document.querySelector('.main-container'),
        document.querySelector('.posts-list-container'),
        document.querySelector('.page-container'),
        document.querySelector('.content'),
        document.querySelector('main'),
        document.body
      ]

      for (const container of possibleContainers) {
        if (container) {
          // Vérifier si ce conteneur a effectivement un scroll
          const hasScroll = container.scrollHeight > container.clientHeight

          if (hasScroll) {
            this.scrollContainer = container
            return
          }
        }
      }

      // Fallback sur body
      this.scrollContainer = document.body
    },

    // Bloquer le scroll du conteneur principal (pour les popups de filtres)
    blockMainScroll() {
      if (this.isMainScrollBlocked) {
        return
      }

      this.isMainScrollBlocked = true

      // Sauvegarder les positions de scroll et styles
      if (this.scrollContainer) {
        this.savedScrollTop = this.scrollContainer.scrollTop
        this.savedOverflow = this.scrollContainer.style.overflow
      }

      this.savedBodyOverflow = document.body.style.overflow
      this.savedHtmlOverflow = document.documentElement.style.overflow

      // BLOQUER COMPLÈTEMENT le scroll sur tous les conteneurs
      if (this.scrollContainer) {
        this.scrollContainer.style.overflow = 'hidden'
        this.scrollContainer.style.position = 'relative'
      }

      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'

      // Ajouter un event listener pour empêcher le scroll par wheel/touch
      // UNIQUEMENT pour wheel et touchmove, pas pour les autres événements
      this.preventScrollHandler = (e) => {
        // Vérifier si l'événement cible un champ input
        const isInputField = e.target.tagName === 'INPUT' ||
                            e.target.tagName === 'TEXTAREA' ||
                            e.target.isContentEditable

        // NE JAMAIS bloquer les événements sur les champs input
        if (isInputField) {
          return
        }

        // Vérifier si le scroll provient d'un élément scrollable dans la popup
        const isInsideDropdown = e.target.closest('.el-select-dropdown') ||
                                  e.target.closest('.el-scrollbar__wrap') ||
                                  e.target.closest('.el-cascader-menu') ||
                                  e.target.closest('.el-picker-panel') ||
                                  e.target.closest('.el-popper')

        const isInsideFilterPopup = e.target.closest('.filter-popup')

        // Autoriser le scroll dans les dropdowns/cascaders
        if (isInsideDropdown) {
          return
        }

        // Pour les autres éléments de la popup (checkboxes, radios, etc.)
        // Vérifier s'il y a un scrollbar dans la popup elle-même
        if (isInsideFilterPopup) {
          const popup = e.target.closest('.filter-popup')
          if (popup && popup.scrollHeight > popup.clientHeight) {
            return
          }
        }

        // Bloquer le scroll partout ailleurs
        e.preventDefault()
        e.stopPropagation()
      }

      // IMPORTANT : Écouter UNIQUEMENT wheel et touchmove, pas les autres événements
      window.addEventListener('wheel', this.preventScrollHandler, { passive: false, capture: true })
      window.addEventListener('touchmove', this.preventScrollHandler, { passive: false, capture: true })
    },

    // Débloquer le scroll du conteneur principal (pour les popups de filtres)
    unblockMainScroll() {
      if (!this.isMainScrollBlocked) {
        return
      }

      this.isMainScrollBlocked = false

      // Retirer les event listeners
      if (this.preventScrollHandler) {
        window.removeEventListener('wheel', this.preventScrollHandler, { capture: true })
        window.removeEventListener('touchmove', this.preventScrollHandler, { capture: true })

        this.preventScrollHandler = null
      }

      // Restaurer les styles
      if (this.scrollContainer) {
        this.scrollContainer.style.overflow = this.savedOverflow || ''
        this.scrollContainer.style.position = ''

        // Restaurer la position de scroll
        if (this.savedScrollTop !== undefined) {
          this.scrollContainer.scrollTop = this.savedScrollTop
        }
      }

      document.body.style.overflow = this.savedBodyOverflow || ''
      document.documentElement.style.overflow = this.savedHtmlOverflow || ''
    },


    // Sauvegarder les valeurs actuelles avant d'ouvrir une popup
    savePopupInitialValues(fieldKey) {
      // Créer une copie profonde de la valeur actuelle du champ
      const currentValue = this.filterValues[fieldKey]
      try {
        this.popupInitialValues[fieldKey] = JSON.parse(JSON.stringify(currentValue === undefined ? null : currentValue))
      } catch (error) {
        console.warn(`[savePopupInitialValues] Erreur lors de la sérialisation pour ${fieldKey}:`, error)
        this.popupInitialValues[fieldKey] = currentValue
      }
    },

    // Vérifier si des changements ont été effectués dans la popup
    hasPopupChanges(fieldKey) {
      const initialValue = this.popupInitialValues[fieldKey]
      const currentValue = this.filterValues[fieldKey] === undefined ? null : this.filterValues[fieldKey]

      try {
        // Comparer les valeurs (conversion en JSON pour comparaison profonde)
        return JSON.stringify(initialValue) !== JSON.stringify(currentValue)
      } catch (error) {
        console.warn(`[hasPopupChanges] Erreur lors de la comparaison pour ${fieldKey}:`, error)
        // En cas d'erreur, considérer qu'il y a eu des changements (sécurité)
        return true
      }
    },

    //  OPTIMISÉ : Fermeture avec recherche automatique seulement si changements
    closePopupWithSearch(forceSearch = false) {
      // Sauvegarder le champ actif avant de fermer
      const currentField = this.activePopup

      // Vérifier si des changements ont été effectués
      const hasChanges = currentField && this.hasPopupChanges(currentField)

      // Sauvegarder les filtres avant de fermer
      this.saveFilters()

      // Débloquer le scroll avant de fermer la popup
      this.unblockMainScroll()

      // Fermer la popup
      this.activePopup = null
      this.popupStyle = {}
      this.popupInitialValues = {}

      // Déclencher la recherche seulement si des changements ont été détectés ou si forcé
      if (forceSearch || hasChanges) {
        this.emitSearch()
      }
    },

    //  Gérer l'événement close-and-search (fermeture explicite avec recherche forcée)
    handleCloseAndSearch() {
      // Forcer la recherche car c'est une demande explicite du composant enfant (ex: bouton Valider)
      this.closePopupWithSearch(true)
    },

    //  BACKUP : Garder handlePopupClosed pour compatibilité
    handlePopupClosed(event) {
      if (event && event.shouldTriggerSearch) {
        // Forcer la recherche car c'est une demande explicite du composant enfant
        this.closePopupWithSearch(true)
      }
    },

    //  FORCER le z-index des dropdowns Element UI
    forceDropdownZIndex() {
      this.$nextTick(() => {
        // Trouver tous les dropdowns Element UI
        const dropdowns = document.querySelectorAll('.el-select-dropdown, .el-popper, .api-search-select-dropdown')
        dropdowns.forEach(dropdown => {
          if (dropdown) {
            this.applyDropdownFixes(dropdown)
          }
        })
      })
    },


    //  Observer les dropdowns créés dynamiquement
    setupDropdownObserver() {
      if (this.dropdownObserver) {
        this.dropdownObserver.disconnect()
      }

      this.dropdownObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Nœuds ajoutés (dropdown ouvert)
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              if (node.classList && (
                node.classList.contains('el-select-dropdown') ||
                node.classList.contains('el-popper') ||
                node.classList.contains('api-search-select-dropdown')
              )) {
                // Juste appliquer les fixes visuels, pas de blocage de scroll
                this.applyDropdownFixes(node)
              }
            }
          })
        })
      })

      this.dropdownObserver.observe(document.body, {
        childList: true,
        subtree: true
      })
    },


    //  Écouter les événements Element UI - VERSION SIMPLE
    setupElementUIEventListeners() {
      // Écouter seulement les clics sur les selects
      document.addEventListener('click', (event) => {
        const target = event.target

        // Si on clique sur un select Element UI dans nos popups
        if (this.activePopup && (target.closest('.el-select') || target.closest('.el-input'))) {

          // Forcer seulement le z-index après un délai
          setTimeout(() => {
            this.forceDropdownZIndex()
          }, 100)
        }
      })
    },

    //  Écouter les événements clavier pour fermer les popups
    setupKeyboardListeners() {
      this.keyboardHandler = (event) => {
        // Fermer la popup avec recherche si Escape est pressé
        if (event.key === 'Escape' && this.activePopup) {
          event.preventDefault()
          this.closePopupWithSearch()
        }
      }

      document.addEventListener('keydown', this.keyboardHandler)
    },

    //  Appliquer les corrections sur un dropdown - VERSION CIBLÉE
    applyDropdownFixes(dropdown) {
      // Z-index pour la visibilité - TRÈS ÉLEVÉ
      dropdown.style.zIndex = '999999'

      // Corriger les scrollbars multiples
      const scrollbars = dropdown.querySelectorAll('.el-scrollbar')
      scrollbars.forEach(scrollbar => {
        scrollbar.style.maxHeight = 'none'

        const wrap = scrollbar.querySelector('.el-scrollbar__wrap')
        if (wrap) {
          wrap.style.overflowX = 'hidden'
          wrap.style.maxHeight = '280px'
        }
      })

      // S'assurer que le champ de recherche est interactif
      const searchInput = dropdown.querySelector('.el-input__inner')
      if (searchInput) {
        searchInput.style.pointerEvents = 'auto'
        searchInput.style.background = 'white'
        searchInput.style.border = '1px solid #dcdfe6'

        // Ajouter un event listener pour déboguer
        searchInput.addEventListener('input', (e) => {
        })
      }

    },

    isCascaderPopup(button) {
      return button && (
        button.field.component === 'GenericFieldObjectTypes' ||
        button.field.component === 'SearchFieldObjectTypes'
      )
    },

    calculatePopupPosition(event) {
      if (event && event.target) {
        this.$nextTick(() => {
          const rect = event.target.getBoundingClientRect()
          const viewportWidth = window.innerWidth

          //  TRAITEMENT SPÉCIAL POUR LES CASCADERS
          const activeButton = this.allFilterButtons.find(b => b.key === this.activePopup)
          const isCascader = activeButton && (
            activeButton.field.component === 'GenericFieldObjectTypes' ||
            activeButton.field.component === 'SearchFieldObjectTypes'
          )

          const popupWidth = isCascader ? 600 : 400 // Largeur plus grande pour les cascaders

          let left = rect.left

          // Si la popup déborde à droite, la décaler à gauche
          if (left + popupWidth > viewportWidth) {
            left = viewportWidth - popupWidth - 20
          }

          this.popupStyle = {
            position: 'fixed',
            top: rect.bottom + 8 + 'px',
            left: Math.max(20, left) + 'px',
            zIndex: 19999,
            ...(isCascader && { minWidth: '600px', width: 'auto' }) // Styles spéciaux pour cascaders
          }

        })
      }
    },

    // === RECHERCHE ===
    syncSearchFilterFromInput({ commitInput = false } = {}) {
      const trimmedSearch = (this.searchValue || '').trim()

      if (commitInput) {
        this.$set(this.filterValues, 'search', trimmedSearch || '')
        return
      }

      // Hors validation explicite : vider le filtre actif si le champ est vide,
      // sans appliquer une saisie non validée lors d'un autre changement de filtre.
      if (!trimmedSearch && Object.prototype.hasOwnProperty.call(this.filterValues, 'search')) {
        this.$set(this.filterValues, 'search', '')
      }
    },

    handleSearchInput() {
      const hadActiveSearch = Boolean((this.filterValues.search || '').trim())
      this.syncSearchFilterFromInput()

      if (!(this.searchValue || '').trim() && hadActiveSearch) {
        this.saveFilters()
        this.emitSearch()
      }
    },

    handleSearchEnter(event) {
      //  Gérer proprement la touche Entrée
      event.preventDefault()
      this.handleSearch()
    },

    handleSearch() {
      this.syncSearchFilterFromInput({ commitInput: true })
      this.saveFilters()
      this.emitSearch()
    },

    emitSearch() {
      this.syncSearchFilterFromInput()

      //  NETTOYAGE : Convertir les filtres API vers le format Store et supprimer les valeurs null
      const cleanedFilters = convertApiFiltersToStore(this.filterValues, {
        metier: this.userContext.metier || (this.$store && this.$store.getters && this.$store.getters.getSitimmoUser && this.$store.getters.getSitimmoUser.metier)
      })

      //  SUPPRESSION des valeurs null pour éviter les problèmes avec l'API
      // GARDER les undefined car ils servent à supprimer les champs du store
      // SAUF pour beforeContacts et afterContacts qui doivent être préservés même s'ils sont null
      const preservedKeys = ['beforeContacts', 'afterContacts']
      Object.keys(cleanedFilters).forEach(key => {
        // Supprimer uniquement les valeurs null (pas undefined)
        // undefined = signal pour supprimer du store
        if (cleanedFilters[key] === null && !preservedKeys.includes(key)) {
          delete cleanedFilters[key]
        }
      })

      // 🔒 SUPPRESSION EXPLICITE : Marquer les clés absentes de filterValues comme undefined
      // pour forcer leur suppression du store (car addFilter fait un merge)
      // Récupérer TOUS les champs disponibles dans le formulaire dynamiquement
      const allPossibleKeys = []
      if (this.formConfig && this.formConfig.sections) {
        this.formConfig.sections.forEach(section => {
          if (section.fields) {
            section.fields.forEach(field => {
              allPossibleKeys.push(field.name)
              // Ajouter aussi les champs dérivés (pour les ranges)
              if (field.eventMapping) {
                const mapping = typeof field.eventMapping === 'string'
                  ? JSON.parse(field.eventMapping)
                  : field.eventMapping
                if (mapping.minField) allPossibleKeys.push(mapping.minField)
                if (mapping.maxField) allPossibleKeys.push(mapping.maxField)
              }
            })
          }
        })
      }

      // Ajouter les champs générés par conversion (pas dans le formulaire mais dans le store)
      allPossibleKeys.push('negociateur') // Utilisé par contacts.js
      allPossibleKeys.push('negociateur_code') // Généré par convertNegociateurField()

      console.log('🔑 [emitSearch] allPossibleKeys:', allPossibleKeys)

      // Liste des clés à EXCLURE de la suppression automatique
      // (car gérées globalement dans le store 'donnees')
      const excludedKeys = ['beforeContacts', 'afterContacts']

      allPossibleKeys.forEach(key => {
        if (excludedKeys.includes(key)) {
          return
        }

        const hasFilterValue = Object.prototype.hasOwnProperty.call(this.filterValues, key)
        const rawValue = this.filterValues[key]
        const isEmptyValue = rawValue === null
          || rawValue === undefined
          || rawValue === ''
          || (Array.isArray(rawValue) && rawValue.length === 0)

        // Absent ou vidé (null, [], '') : marquer undefined pour retirer du store (addFilter merge)
        // @todo: attention ! on se retrouve avec des dégueulasseries dans l'url
        // https://espace-client-backend.ubiflow.net/annonce?champsRechercheLibre[]=ville&champsRechercheLibre[]=titre&champsRechercheLibre[]=reference&page=1&perPage=10&rechercheLibre=undefined&orderBy=-DC&etat=A&dataValues[CONTACT_A_AFFICHER]=undefined
        // Toujours valider en PROD !
        const rangeBoundKeys = [
          'surfaceGt', 'surfaceLt',
          'priceGt', 'priceLt',
          'surfaceProgrammeMin', 'surfaceProgrammeMax'
        ]
        if (rangeBoundKeys.includes(key)) {
          // Les bornes sont stockées dans le champ range parent (ex: filterValues.price)
          // et déjà extraites par convertApiFiltersToStore : ne pas les effacer ici
          const convertedValue = cleanedFilters[key]
          if (convertedValue !== undefined && convertedValue !== null) {
            return
          }
          if (!hasFilterValue || isEmptyValue) {
            cleanedFilters[key] = undefined
          }
          return
        }
      })

      //  CORRECTION SURFACE : Si surfaceGt existe sans surfaceLt, ajouter une valeur max par défaut
      // L'API Sitimmo ne gère pas correctement surfaceGt seul, il faut toujours les deux valeurs
      if (cleanedFilters.surfaceGt !== undefined && cleanedFilters.surfaceLt === undefined) {
        cleanedFilters.surfaceLt = 10000 // Valeur max du champ surface
      }
      if (cleanedFilters.surfaceLt !== undefined && cleanedFilters.surfaceGt === undefined) {
        cleanedFilters.surfaceGt = 0
      }

      // Même logique pour le prix
      if (cleanedFilters.priceGt !== undefined && cleanedFilters.priceLt === undefined) {
        cleanedFilters.priceLt = 99999999 // Valeur max du champ price
      }
      if (cleanedFilters.priceLt !== undefined && cleanedFilters.priceGt === undefined) {
        cleanedFilters.priceGt = 0
      }

      // Même contrainte Sitimmo pour surfaceProgrammeMin seul (promoteur programmes)
      if (cleanedFilters.surfaceProgrammeMin !== undefined && cleanedFilters.surfaceProgrammeMax === undefined) {
        cleanedFilters.surfaceProgrammeMax = 10000
      }
      if (cleanedFilters.surfaceProgrammeMax !== undefined && cleanedFilters.surfaceProgrammeMin === undefined) {
        cleanedFilters.surfaceProgrammeMin = 0
      }

      // Champ libre hors formConfig : conserver search: '' pour la persistance (comme user, stock, etc.)
      if (!this.hideSearchField && !(this.searchValue || '').trim()) {
        cleanedFilters.search = ''
      }

      this.$emit('search', cleanedFilters)
    },

    // === UTILITAIRES ===
    getComponentForField(field) {
      // Priorité 1 : Utiliser le composant défini par le backend
      if (field.component) {
        return field.component
      }

      // Priorité 2 : Mapping générique par type de champ
      const typeMap = {
        'text': 'GenericFieldText',
        'radio_group': 'GenericFieldRadio',
        'select': 'GenericFieldSelect',
        'multiselect': 'GenericFieldSelect',
        'daterange': 'GenericFieldDateRange',
        'range': 'GenericFieldRange',
        'slider': 'GenericFieldRange',
        'object_types': 'SearchFieldObjectTypes',
        'offer_types': 'SearchFieldOfferTypes'
      }

      const fieldType = field.fieldType && field.fieldType.code
      return typeMap[fieldType] || 'GenericFieldText'
    },

    isVisible(field) {
      // Si le champ a des visibility_rules, les évaluer côté frontend en temps réel
      if (field.visibility_rules && Array.isArray(field.visibility_rules) && field.visibility_rules.length > 0) {
        return this.evaluateVisibilityRules(field.visibility_rules)
      }

      // Sinon, utiliser la valeur par défaut du backend
      return field.isVisible
    },

    evaluateVisibilityRules(rules) {
      // Si pas de règles, le champ est visible
      if (!rules || rules.length === 0) {
        return true
      }

      // Grouper les règles par ruleGroup
      const groupedRules = this.groupRulesByGroup(rules)

      // Évaluer chaque groupe
      const groupResults = []
      for (const [groupName, groupRules] of Object.entries(groupedRules)) {
        const groupResult = this.evaluateRuleGroup(groupRules)
        groupResults.push(groupResult)
      }

      // Tous les groupes doivent être vrais (AND entre les groupes)
      return groupResults.every(result => result)
    },

    groupRulesByGroup(rules) {
      // Grouper les règles par ruleGroup
      const groups = {}

      rules.forEach(rule => {
        const groupName = rule.ruleGroup || 'default'
        if (!groups[groupName]) {
          groups[groupName] = []
        }
        groups[groupName].push(rule)
      })

      return groups
    },

    evaluateRuleGroup(groupRules) {
      // Déterminer l'opérateur logique du groupe (OR ou AND)
      const hasOrOperator = groupRules.some(rule => rule.logicOperator === 'OR')

      if (hasOrOperator) {
        // Si au moins une règle est OR, utiliser la logique OR pour tout le groupe
        return groupRules.some(rule => this.evaluateSingleRule(rule))
      } else {
        // Sinon, utiliser la logique AND (comportement par défaut)
        return groupRules.every(rule => this.evaluateSingleRule(rule))
      }
    },

    evaluateSingleRule(rule) {
      // Évaluer une seule règle
      let targetValue = this.getNestedProperty(rule.field)
      let ruleValue = rule.value
      console.log(`🔍 [evaluateSingleRule] AVANT normalisation: field="${rule.field}" targetValue=${JSON.stringify(targetValue)} (type=${typeof targetValue}) ruleValue=${JSON.stringify(ruleValue)} (type=${typeof ruleValue})`)
      // Normaliser les valeurs boolean (string "true"/"false" -> boolean)
      if (ruleValue === 'true' || ruleValue === 'false') {
        ruleValue = ruleValue === 'true'
      }
      if (targetValue === 'true' || targetValue === 'false') {
        targetValue = targetValue === 'true'
      }

      let result = false

      switch (rule.operator) {
        case 'equals':
        case '=':
        case '==':
          result = targetValue == ruleValue // eslint-disable-line eqeqeq
          break
        case 'not_equals':
        case '!=':
          result = targetValue != ruleValue // eslint-disable-line eqeqeq
          break
        case 'contains':
          result = Array.isArray(targetValue) && targetValue.includes(ruleValue)
          break
        case 'not_contains':
          result = Array.isArray(targetValue) && !targetValue.includes(ruleValue)
          break
        case 'greater_than':
        case '>':
          result = Number(targetValue) > Number(ruleValue)
          break
        case 'greater_than_or_equal':
        case '>=':
          result = Number(targetValue) >= Number(ruleValue)
          break
        case 'less_than':
        case '<':
          result = Number(targetValue) < Number(ruleValue)
          break
        case 'less_than_or_equal':
        case '<=':
          result = Number(targetValue) <= Number(ruleValue)
          break
        case 'in': {
          const ruleVals = Array.isArray(ruleValue)
            ? ruleValue
            : String(ruleValue).split(',').map(s => s.trim()).filter(Boolean)
          if (Array.isArray(targetValue)) {
            result = ruleVals.some(rv => targetValue.includes(rv))
          } else {
            result = ruleVals.includes(String(targetValue))
          }
          break
        }
        case 'not_in': {
          const ruleVals = Array.isArray(ruleValue)
            ? ruleValue
            : String(ruleValue).split(',').map(s => s.trim()).filter(Boolean)
          if (Array.isArray(targetValue)) {
            result = !ruleVals.some(rv => targetValue.includes(rv))
          } else {
            result = !ruleVals.includes(String(targetValue))
          }
          break
        }
        default:
          console.warn(`[evaluateSingleRule] Opérateur inconnu: ${rule.operator}`)
          result = true
      }

      // Appliquer showWhenTrue
      const finalResult = rule.showWhenTrue ? result : !result

      console.log(`🔍 [evaluateSingleRule] field="${rule.field}" operator="${rule.operator}" targetValue=${JSON.stringify(targetValue)} ruleValue=${JSON.stringify(ruleValue)} result=${result} showWhenTrue=${rule.showWhenTrue} finalResult=${finalResult}`)

      return finalResult
    },

    getNestedProperty(path) {
      // Récupérer une valeur imbriquée à partir d'un chemin (ex: "userContext.activeName" ou "betaTesteur")
      const parts = path.split('.')
      let value

      // Vérifier d'abord dans userContext si le chemin commence par "userContext."
      if (parts[0] === 'userContext') {
        value = this.userContext
        for (let i = 1; i < parts.length; i++) {
          if (value && value[parts[i]] !== undefined) {
            value = value[parts[i]]
          } else {
            return undefined
          }
        }
        return value
      }

      // Pour les règles de visibilité, chercher d'abord dans userContext (clé plate)
      // (pour éviter les conflits avec les valeurs de filterValues)
      if (this.userContext && this.userContext[path] !== undefined) {
        return this.userContext[path]
      }

      // Contexte complet (users_count, contacts_count, "user.roles", stocks_count, etc.)
      const rulesCtx = this.visibilityRulesContext
      if (rulesCtx && Object.prototype.hasOwnProperty.call(rulesCtx, path)) {
        return rulesCtx[path]
      }

      // Pour les chemins comme "sitimmoUser.stocks", chercher dans le store
      if (parts.length > 1 && parts[0] === 'sitimmoUser') {
        if (this.$store && this.$store.getters.getSitimmoUser) {
          const sitimmoUser = this.$store.getters.getSitimmoUser
          // Naviguer dans l'objet sitimmoUser
          value = sitimmoUser
          for (let i = 1; i < parts.length; i++) {
            if (value && value[parts[i]] !== undefined) {
              // Pour stocks, retourner la longueur du tableau
              if (parts[i] === 'stocks' && Array.isArray(value[parts[i]])) {
                return value[parts[i]].length
              }
              value = value[parts[i]]
            } else {
              break
            }
          }
          if (value !== sitimmoUser) {
            return value
          }
        }
      }

      // Sinon vérifier dans filterValues
      value = this.filterValues
      for (const part of parts) {
        if (value && value[part] !== undefined) {
          value = value[part]
        } else {
          return undefined
        }
      }
      return value
    },

    translate(label) {
      if (label && label.includes('.')) {
        return this.$t(label)
      }
      return label
    },

    getButtonDisplayText(button) {
      const value = this.filterValues[button.key]
      const baseLabel = this.translate(button.label)

      if (!value || (Array.isArray(value) && value.length === 0)) {
        return baseLabel
      }

      //  GESTION SPÉCIFIQUE par type de champ
      const fieldType = button.field.fieldType && button.field.fieldType.code

      // Radio groups - afficher la valeur sélectionnée
      if (fieldType === 'radio_group') {
        const options = this.getFieldOptions(button.field)
        const selectedOption = options.find(opt => opt.value === value)
        if (selectedOption) {
          return `${baseLabel} : ${this.translate(selectedOption.label)}`
        }

        //  Si pas d'option trouvée et que c'est un champ store, essayer de récupérer depuis le store
        if (button.field.dataSource && button.field.dataSource.type === 'store') {
          const storeData = this.getStoreDataForField(button.field)
          const storeOption = storeData.find(opt => opt.value === value)
          if (storeOption) {
            return `${baseLabel} : ${storeOption.label}`
          }
        }

        return `${baseLabel} : ${value}`
      }

      // MONTH PICKER - afficher le nom du mois
      if (fieldType === 'monthpicker') {
        // Le monthPicker est stocké comme un tableau [startDate, endDate]
        if (Array.isArray(value) && value.length === 2 && value[0]) {
          // Extraire le mois depuis startDate (format "YYYY-MM-DD")
          const startDate = new Date(value[0])
          const year = startDate.getFullYear()
          const month = startDate.getMonth() // 0-11
          const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
          const monthName = monthNames[month]
          return `${baseLabel} : ${monthName} ${year}`
        }
        return baseLabel
      }

      //  DATES - traiter en priorité avant les arrays (dateCreation ou dateRange)
      if (Array.isArray(value) && (button.field.name === 'dateCreation' || button.field.name === 'dateRange')) {
        if (value.length === 2 && value[0] && value[1]) {
          const startDate = new Date(value[0]).toLocaleDateString('fr-FR')
          const endDate = new Date(value[1]).toLocaleDateString('fr-FR')
          return `${baseLabel} : ${startDate} - ${endDate}`
        }
        return baseLabel // Si pas de dates valides, afficher juste le label
      }

      // Ranges (surface, price)
      if ((button.field.name === 'surface' || button.field.name === 'price') && value && typeof value === 'object') {
        const unit = button.field.name === 'price' ? '€' : 'm²'
        const min = Array.isArray(value)
          ? value[0]
          : (value.min != null ? value.min : (value.surfaceGt != null ? value.surfaceGt : value.priceGt))
        const max = Array.isArray(value)
          ? value[1]
          : (value.max != null ? value.max : (value.surfaceLt != null ? value.surfaceLt : value.priceLt))
        const hasMin = min !== null && min !== undefined && min !== ''
        const hasMax = max !== null && max !== undefined && max !== ''

        if (hasMin && hasMax) {
          return `${baseLabel} : ${min} - ${max} ${unit}`
        } else if (hasMin) {
          return `${baseLabel} : > ${min} ${unit}`
        } else if (hasMax) {
          return `${baseLabel} : < ${max} ${unit}`
        }
      }

      // Selects multiples - afficher le nombre (APRÈS les dates)
      if (Array.isArray(value)) {
        return `${baseLabel} (${value.length})`
      }

      // Selects simples - afficher la valeur
      if (fieldType === 'select') {
        // Vérifier d'abord si c'est un champ de type 'store'
        if (button.field.dataSource && button.field.dataSource.type === 'store') {
          const storeData = this.getStoreDataForField(button.field)
          const selectedOption = storeData.find(opt => String(opt.value) === String(value))
          if (selectedOption) {
            return `${baseLabel} : ${selectedOption.label}`
          }
        } else {
          // Sinon, utiliser les options statiques
          const options = this.getFieldOptions(button.field)
          const selectedOption = options.find(opt => String(opt.value) === String(value))
          if (selectedOption) {
            return `${baseLabel} : ${this.translate(selectedOption.label)}`
          }
        }
        return `${baseLabel} : ${value}`
      }

      // Fallback
      return `${baseLabel} ✓`
    },

    getFieldOptions(field) {
      //  PRIORITÉ 1 : Options dans dataSource (cas général)
      if (field.dataSource && field.dataSource.data) {
        return field.dataSource.data
      }

      //  PRIORITÉ 2 : Options dans properties (cas CMI pour typeObjets)
      if (field.properties && field.properties.options) {
        return field.properties.options
      }

      //  PRIORITÉ 3 : Options dans properties sous forme d'objet (legacy)
      if (field.properties && Array.isArray(field.properties)) {
        // Cas où properties est un array d'options
        return field.properties
      }

      return []
    },

    getStoreDataForField(field) {
      // Récupérer les données depuis le Vuex store pour les champs de type 'store'
      if (!field.dataSource || field.dataSource.type !== 'store') {
        return []
      }

      const params = field.dataSource.parameters
      if (!params) return []

      try {
        // Récupérer les données depuis le store
        let storeData = []
        if (params.storeGetter && this.$store.getters[params.storeGetter]) {
          storeData = this.$store.getters[params.storeGetter]
        } else if (params.storePath) {
          // Naviguer dans le store avec le path
          const pathParts = params.storePath.split('.')
          let current = this.$store.state
          for (const part of pathParts) {
            if (current && current[part]) {
              current = current[part]
            } else {
              current = null
              break
            }
          }
          if (Array.isArray(current)) {
            storeData = current
          }
        }

        // Formater les données
        if (Array.isArray(storeData)) {
          return storeData.map(item => {
            const valueField = params.valueField || 'id'
            const labelField = params.labelField || 'name'
            const value = item[valueField]
            const rawLabel = item[labelField] || item['name'] || item['nom'] || item['label'] || item['libelle'] || `Stock ${value}`
            const label = typeof rawLabel === 'string' && !rawLabel.includes('.')
              ? capitalizeFirstLetter(rawLabel)
              : rawLabel

            return {
              value: String(value), // Convertir en string pour cohérence
              label: label
            }
          })
        }

        return []
      } catch (error) {
        console.error('Erreur lors de la récupération des données store:', error)
        return []
      }
    }
  },

  beforeDestroy() {
    // Nettoyer l'observateur
    if (this.dropdownObserver) {
      this.dropdownObserver.disconnect()
    }

    // Débloquer le scroll au cas où
    this.unblockMainScroll()

    //  Nettoyer le listener clavier
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler)
    }

    //  Nettoyer le watcher du tri
    if (this.sortWatcherUnwatch) {
      this.sortWatcherUnwatch()
    }
  }
}
</script>

<style lang="scss" scoped>
.api-search-form {
  width: 100%;
  margin-bottom: 30px; // Plus d'espacement avec le tableau

  .search-panel {
    background-color: #f5f5f5;
    border: none;
    box-shadow: none;
    border-radius: 0;
    padding: 0;
    margin: 0;
    width: 100%;
  }

  .error-container {
    margin: 16px;
  }

  .search-container {
    padding: 8px 16px;
    width: 100%;
  }

  .filters-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    &.centered {
      justify-content: center;
      width: 100%;
    }
  }

  .search-field-container {
    .el-input {
      .el-input__inner {
        font-size: 15px; //  UI : Augmentation de 14px → 15px
        height: 36px; //  UI : Augmentation de 32px → 36px
        border: 1px solid #606266;
        border-radius: 4px 0 0 4px;
      }

      .el-input-group__append {
        border: 1px solid #606266;
        border-left: none;
        border-radius: 0 4px 4px 0;
        padding: 0 12px; //  UI : Augmentation de 8px → 12px
        cursor: pointer;

        .el-icon-search {
          font-size: 16px; //  UI : Icône plus grande
          color: #909399;

          &:hover {
            color: #409eff;
          }
        }
      }
    }
  }

  .filter-button-container {
    position: relative;
  }

  .filter-btn {
    font-size: 15px; //  UI : Augmentation de 14px → 15px
    height: 36px; //  UI : Augmentation de 32px → 36px
    padding: 0 14px; //  UI : Augmentation de 12px → 14px
    min-width: 120px; //  UI : Largeur minimale pour meilleure visibilité
    border: 1px solid #dcdfe6;
    background: white;
    color: #606266;

    .clear-btn {
      margin-left: 8px;
      font-size: 12px;
      opacity: 0.7;

      &:hover {
        opacity: 1;
        color: #f56c6c;
      }
    }

    &.has-value {
      border-left: 3px solid #0ab1bf;
      background: rgba(10, 177, 191, 0.1);
      color: #0ab1bf;
      font-weight: 500;
    }

    &:hover {
      border-color: #c0c4cc;
    }
  }

  .more-filters-container {
    margin-left: auto;
  }

  // === POPUPS ===
  .filter-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 19998;
    background: transparent;
  }

  .filter-popup {
    background: white;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 280px;
    max-width: 450px;

    //  TRAITEMENT SPÉCIAL POUR LES CASCADERS
    &.cascader-popup {
      //  NE PAS ÉCRASER LES PROPRIÉTÉS DE POSITIONNEMENT
      // position, top, left, z-index viennent de popupStyle et doivent être préservées

      // Seulement ajuster l'apparence
      padding: 0 !important;
      min-width: 600px !important;
      max-width: none !important;
      width: auto !important;
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;

      //  FLÈCHES SPÉCIALES POUR LES CASCADERS
      // Pointer vers le cascader lui-même (qui a un fond blanc)
      &::before {
        border-bottom-color: #e4e7ed !important; // Couleur de bordure du cascader
        top: -12px !important; // Même position que les autres popups
      }

      &::after {
        border-bottom-color: white !important; // Couleur de fond du cascader
        top: -10px !important; // Légèrement plus bas pour masquer la bordure
        border-left-width: 12px !important; // Légèrement plus large pour bien masquer
        border-right-width: 12px !important;
        left: 20px !important; // Ajuster la position pour centrer
      }

      //  MASQUER LA BORDURE DU CASCADER LÀ OÙ LA FLÈCHE SE CONNECTE
      ::v-deep .el-cascader-panel {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: -1px;
          left: 30px;
          width: 24px;
          height: 2px;
          background: white;
          z-index: 10;
        }
      }
    }

    // Flèche pointant vers le bouton (plus grosse comme dans l'ancienne mouture)
    &::before {
      content: '';
      position: absolute;
      top: -12px;
      left: 20px;
      width: 0;
      height: 0;
      border-left: 12px solid transparent;
      border-right: 12px solid transparent;
      border-bottom: 12px solid #dcdfe6;
    }

    &::after {
      content: '';
      position: absolute;
      top: -11px;
      left: 21px;
      width: 0;
      height: 0;
      border-left: 11px solid transparent;
      border-right: 11px solid transparent;
      border-bottom: 11px solid white;
    }
  }

  // === Z-INDEX FIXES pour les selects dans les popups ===

  //  CRITIQUE : Assurer que les dropdowns Element-UI sont au-dessus des popups
  ::v-deep .el-select-dropdown {
    z-index: 999999 !important;
    min-width: 280px !important; // Largeur minimale pour éviter le crop
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;
  }

  ::v-deep .el-popper {
    z-index: 999999 !important;
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;
  }

  ::v-deep .el-picker-panel {
    z-index: 999999 !important;
  }

  //  Spécifique aux selects dans les popups de filtres
  .filter-popup ::v-deep .el-select-dropdown {
    z-index: 9999999 !important;
    min-width: 320px !important; // Plus large dans les popups
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;
  }

  .filter-popup ::v-deep .el-popper {
    z-index: 9999999 !important;
  }

  //  Assurer que les options sont visibles et cliquables
  .filter-popup ::v-deep .el-select-dropdown__item {
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;
    padding: 10px 16px !important; // Plus de padding
    font-size: 15px !important; // Taille cohérente avec le reste
    font-weight: 400 !important;
    line-height: 1.5 !important;
    cursor: pointer !important;
    pointer-events: auto !important;
    white-space: normal !important; // Permettre le retour à la ligne si besoin
    word-wrap: break-word !important;
    min-height: 40px !important; // Hauteur minimale pour les noms longs

    // NE PAS forcer display: flex, Element UI utilise display: none pour filtrer
    &:not([style*="display: none"]) {
      display: flex !important;
      align-items: center !important;
    }

    &:hover {
      background-color: #f5f7fa !important;
    }

    &.selected {
      background-color: #409eff !important;
      color: white !important;
    }
  }

  //  Style pour les selects filtrables
  .filter-popup ::v-deep .el-select .el-input .el-input__inner {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
  }

  .filter-popup ::v-deep .el-select .el-input.is-focus .el-input__inner {
    border-color: #409eff;
  }

  //  Styles globaux pour les dropdowns des selects API
  ::v-deep .api-search-select-dropdown {
    z-index: 99999999 !important;
    position: absolute !important;
    pointer-events: auto !important;
    min-width: 320px !important; // Largeur minimale
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;

    .el-select-dropdown__item {
      font-family: "Dosis", Helvetica, Arial, sans-serif !important;
      padding: 10px 16px !important;
      font-size: 15px !important; // Cohérent avec le reste de l'interface
      font-weight: 400 !important;
      line-height: 1.5 !important;
      cursor: pointer !important;
      pointer-events: auto !important;
      white-space: normal !important; // Permettre le retour à la ligne
      word-wrap: break-word !important;
      min-height: 40px !important;

      // NE PAS forcer display: flex, Element UI utilise display: none pour filtrer
      &:not([style*="display: none"]) {
        display: flex !important;
        align-items: center !important;
      }

      &:hover {
        background-color: #f5f7fa !important;
      }

      &.selected {
        background-color: #409eff !important;
        color: white !important;
      }
    }

    //  CHAMP DE RECHERCHE dans les dropdowns
    .el-input {
      pointer-events: auto !important;
      z-index: 99999999 !important;

      .el-input__inner {
        pointer-events: auto !important;
        z-index: 99999999 !important;
        background: white !important;
        border: 1px solid #dcdfe6 !important;
        cursor: text !important; // Curseur texte
        user-select: text !important; // Permettre la sélection de texte

        &:focus {
          border-color: #409eff !important;
          outline: none !important;
        }
      }
    }

    // Permettre explicitement la saisie dans les champs de recherche des dropdowns
    .el-select__input {
      pointer-events: auto !important;
      cursor: text !important;
      user-select: text !important;
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
    }

    // S'assurer que les options du dropdown sont visibles (mais pas forcer display: flex)
    .el-select-dropdown__item:not([style*="display: none"]) {
      display: flex !important;
      align-items: center !important;
    }

    // Ne pas masquer le champ de recherche
    .el-select .el-input.is-focus .el-input__inner,
    .el-select .el-input__inner {
      pointer-events: auto !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
  }

  //  Styles pour les dropdowns Element UI
  ::v-deep .el-popper[x-placement] {
    z-index: 999999 !important;
  }

  ::v-deep .el-select-dropdown {
    z-index: 999999 !important;
  }

  //  Styles spécifiques pour les dropdowns de nos selects
  ::v-deep .api-search-select-dropdown {
    z-index: 999999 !important;
    max-height: 400px !important; // Plus de hauteur pour voir plus d'options
    overflow-y: auto !important;
    min-width: 320px !important;
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;

    // Corriger les scrollbars multiples
    .el-scrollbar {
      max-height: none !important;

      .el-scrollbar__wrap {
        overflow-x: hidden !important;
        max-height: 360px !important; // Ajusté avec la nouvelle hauteur max
      }
    }

    // S'assurer que la recherche fonctionne
    .el-input {
      pointer-events: auto !important;

      .el-input__inner {
        background: white !important;
        border: 1px solid #dcdfe6 !important;
        pointer-events: auto !important;
        font-size: 15px !important;
        font-family: "Dosis", Helvetica, Arial, sans-serif !important;
        font-weight: 400 !important;
        cursor: text !important;
        user-select: text !important;

        &:focus {
          border-color: #409eff !important;
          outline: none !important;
        }
      }
    }

    // Champ de recherche spécifique dans les dropdowns Element UI
    .el-select-dropdown__wrap {
      pointer-events: auto !important;

      .el-input {
        pointer-events: auto !important;

        .el-input__inner {
          pointer-events: auto !important;
          cursor: text !important;
        }
      }
    }

    // Options cliquables
    .el-select-dropdown__item {
      cursor: pointer !important;
      pointer-events: auto !important;
      padding: 10px 16px !important;
      font-size: 15px !important;
      font-family: "Dosis", Helvetica, Arial, sans-serif !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      min-height: 40px !important;
      white-space: normal !important;
      word-wrap: break-word !important;

      // NE PAS forcer display: flex, Element UI utilise display: none pour filtrer
      &:not([style*="display: none"]) {
        display: flex !important;
        align-items: center !important;
      }
    }
  }

  //  STYLES SPÉCIAUX pour les dropdowns dans les popups de filtres
  .filter-popup {
    // Assurer que les dropdowns restent dans la popup
    ::v-deep .el-select-dropdown {
      position: absolute !important;
      z-index: 999 !important;
      max-height: 300px !important; // Plus de hauteur
      overflow-y: auto !important;
      background: white !important;
      border: 1px solid #e4e7ed !important;
      border-radius: 4px !important;
      box-shadow: 0 2px 12px 0 rgba(0,0,0,.1) !important;
      min-width: 320px !important; // Largeur minimale
      font-family: "Dosis", Helvetica, Arial, sans-serif !important;
    }

    // Forcer la visibilité des options
    ::v-deep .el-select-dropdown__item {
      visibility: visible !important;
      opacity: 1 !important;
      position: relative !important;
      z-index: 1000 !important;
      padding: 10px 16px !important;
      font-size: 15px !important;
      font-family: "Dosis", Helvetica, Arial, sans-serif !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      min-height: 40px !important;
      white-space: normal !important;
      word-wrap: break-word !important;

      // NE PAS forcer display: flex, Element UI utilise display: none pour filtrer
      &:not([style*="display: none"]) {
        display: flex !important;
        align-items: center !important;
      }
    }

    // Input des selects dans les popups
    ::v-deep .el-select .el-input__inner {
      font-family: "Dosis", Helvetica, Arial, sans-serif !important;
      font-size: 15px !important;
      font-weight: 400 !important;
    }
  }

  // === RESPONSIVE ===
  @media (max-width: 768px) {
    .search-container {
      padding: 12px;

      > div:first-child {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;

        .search-field-container {
          .el-input {
            width: 100% !important;
          }
        }

        .more-filters-container {
          margin-left: 0;
          text-align: center;
        }
      }
    }
  }
}
</style>

<style lang="scss">
//  STYLES GLOBAUX pour forcer les dropdowns Element UI au-dessus de tout
.el-popper {
  z-index: 999999 !important;
  min-width: 320px !important;
  font-family: "Dosis", Helvetica, Arial, sans-serif !important;

  // Permettre l'interaction avec les champs de recherche
  .el-input,
  .el-input__inner,
  .el-select__input {
    pointer-events: auto !important;
    cursor: text !important;
    user-select: text !important;
  }
}

.el-select-dropdown {
  z-index: 999999 !important;
  min-width: 320px !important;
  font-family: "Dosis", Helvetica, Arial, sans-serif !important;

  // Permettre l'interaction avec les champs de recherche
  .el-input,
  .el-input__inner,
  .el-select__input {
    pointer-events: auto !important;
    cursor: text !important;
    user-select: text !important;
  }
}

.api-search-select-dropdown {
  z-index: 999999 !important;
  min-width: 320px !important;
  font-family: "Dosis", Helvetica, Arial, sans-serif !important;

  // Permettre l'interaction avec les champs de recherche
  .el-input,
  .el-input__inner,
  .el-select__input {
    pointer-events: auto !important;
    cursor: text !important;
    user-select: text !important;
  }
}

//  UI : AMÉLIORATION TAILLES DE POLICE ET LARGEUR DES CHAMPS
.filter-popup {
  // Labels des champs dans les popups
  .el-form-item__label,
  label {
    font-size: 15px !important; //  UI : Augmentation des labels
    font-weight: 500 !important;
    color: #303133 !important;
  }

  // Champs de saisie
  .el-input__inner {
    font-size: 15px !important; //  UI : Augmentation
    height: 36px !important; //  UI : Augmentation
    line-height: 36px !important;
  }

  // Selects
  .el-select {
    width: 100% !important; //  UI : Largeur complète
    min-width: 240px !important; //  UI : Largeur minimale
    font-family: "Dosis", Helvetica, Arial, sans-serif !important;

    .el-input__inner {
      font-size: 15px !important;
      height: 36px !important;
      font-family: "Dosis", Helvetica, Arial, sans-serif !important;
      font-weight: 400 !important;
    }
  }

  // Radio buttons
  .el-radio {
    font-size: 15px !important; //  UI : Augmentation
    margin-bottom: 10px !important; //  UI : Plus d'espacement

    .el-radio__label {
      font-size: 15px !important;
      padding-left: 8px !important;
    }
  }

  // Checkboxes
  .el-checkbox {
    font-size: 15px !important; //  UI : Augmentation
    margin-bottom: 10px !important; //  UI : Plus d'espacement

    .el-checkbox__label {
      font-size: 15px !important;
      padding-left: 8px !important;
    }
  }

  // Date pickers
  .el-date-editor {
    width: 100% !important; //  UI : Largeur complète

    .el-input__inner {
      font-size: 15px !important;
      height: 36px !important;
    }
  }

  // Range inputs (surface, price)
  .el-input-number {
    .el-input__inner {
      font-size: 15px !important;
      height: 36px !important;
    }
  }
}

//  UI : Options des dropdowns - GLOBAL
.el-select-dropdown {
  min-width: 250px !important; // Largeur minimale pour tous les dropdowns
  font-family: "Dosis", Helvetica, Arial, sans-serif !important; // Police cohérente avec les boutons
}

.el-select-dropdown__item {
  font-family: "Dosis", Helvetica, Arial, sans-serif !important; // Police cohérente
  font-size: 15px !important; //  UI : Augmentation
  font-weight: 400 !important; // Poids normal
  padding: 10px 16px !important; //  UI : Plus d'espacement horizontal
  line-height: 1.5 !important;
  min-height: 40px !important; // Hauteur minimale
  white-space: normal !important; // Permettre le retour à la ligne
  word-wrap: break-word !important;

  // NE PAS forcer display: flex, Element UI utilise display: none pour filtrer
  &:not([style*="display: none"]) {
    display: flex !important;
    align-items: center !important;
  }
}

//  UI : Options des radio/checkbox dans les popups
.generic-field-popup {
  .option-radio,
  .option-checkbox {
    font-size: 15px !important;
    margin-bottom: 10px !important;
  }

  .preset-radio {
    font-size: 15px !important;
  }
}
</style>
